import { useEffect, useRef } from "react";
import { Text, View } from "react-native";
import { useTranslation } from "react-i18next";

import { ChipChoiceRow } from "../primitives/ChipChoiceRow";
import { MiniTrendBars } from "../primitives/MiniTrendBars";
import { useLiveAnnouncement } from "../primitives/useLiveAnnouncement";
import type { UiTheme } from "../theme";
import type { Session } from "@heraion/shared";

import { RHYTHM_KINDS, type RhythmKind, type SessionSummary } from "./progressMath";
import { progressWidgetLabel } from "./progressWidgetCatalog";
import { makeProgressViewStyles } from "./progressViewStyles";
import { useRhythmKindLabels } from "./useRhythmKindLabels";
import { useStatOneColumn } from "./useStatOneColumn";
import { WidgetTitleRow } from "./InfoPill";
import { ProgressWidgetStatus } from "./ProgressWidgetStatus";
import { WeekBarsCard } from "./WeekBarsCard";
import { useDateLocale } from "../primitives/DateFormatContext";

// PG-57: the Rhythm widget, the session counts and the weekly bars as one
// widget (they were two blocks at the foot of Progress). The host fetches
// the sessions. PG-64 fetta 2: Progress shows its last weeks as of today on
// the PG-63 bars (WeekBarsCard: a number on every bar, a tap reads a week);
// the report (`scope="period"`) keeps its whole period on the shared
// MiniTrendBars, the current week drawn as in progress (PG-17), with the
// period's counts. #321: Progress passes `kind`, and the chips on top pick
// the sessions every number counts.
export function ProgressRhythmCard({
  theme,
  summary,
  summaryError,
  rhythmCounts,
  formatDate,
  locale,
  scope = "now",
  partialLast = true,
  nowMs = Date.now(),
  weekStartDay,
  kind,
  onKind,
}: {
  theme: UiTheme;
  /** The period scope reads `total` and `lastSession` only. */
  summary:
    | (Omit<SessionSummary, "lastSession"> & {
        lastSession: Pick<Session, "startedAt"> | null;
      })
    | null;
  summaryError: string | null;
  /** Calendar-week counts, oldest first, the current week last (PG-18). */
  rhythmCounts: number[];
  formatDate: (iso: string, locale: string) => string;
  locale: string;
  scope?: "now" | "period";
  /** The last bar is the week still running. A past period has none. */
  partialLast?: boolean;
  /** The instant the counts end at, to name each week. */
  nowMs?: number;
  /** The athlete's week start, the one the counts were bucketed on. */
  weekStartDay?: number;
  /** The sessions counted; absent with `onKind`, no chips. */
  kind?: RhythmKind;
  onKind?: (kind: RhythmKind) => void;
}) {
  const { t } = useTranslation();
  const dateLocale = useDateLocale();
  const styles = makeProgressViewStyles(theme);
  // Progress only: the report's picture is the same on every phone.
  const oneColumn = useStatOneColumn(scope === "now");
  const kindLabels = useRhythmKindLabels();
  // After a tap on a chip, never at mount nor when the saved choice loads,
  // a screen reader hears the new counts.
  const { speak, region } = useLiveAnnouncement();
  const tapped = useRef(false);
  const announcement =
    kind && summary
      ? t("mobile.progress.rhythmKind.announce", {
          kind: kindLabels[kind],
          total: summary.total,
          last30d: summary.last30d,
          defaultValue: "{{kind}}: {{total}} in total, {{last30d}} in the last 30 days",
        })
      : null;
  useEffect(() => {
    if (!tapped.current || announcement === null) return;
    tapped.current = false;
    speak(announcement);
  }, [kind, announcement, speak]);
  // The widget's name in the panels' title voice, and the place of the ▲
  // when it is an open collapsible widget (PG-57), in every state.
  const titleRow = (
    <WidgetTitleRow theme={theme} title={progressWidgetLabel(t, "rhythm")} />
  );
  if (summaryError !== null || summary === null) {
    return (
      <ProgressWidgetStatus
        theme={theme}
        titleRow={titleRow}
        error={summaryError}
        loading={summary === null}
      />
    );
  }
  // The period's bars carry no numbers (MiniTrendBars is decorative for a
  // screen reader), so the caption under them says this week and the
  // full-week mean. Progress's bars print their numbers: the mean alone.
  const full = partialLast ? rhythmCounts.slice(0, -1) : rhythmCounts;
  const thisWeek =
    partialLast && scope === "period"
      ? t("mobile.progress.rhythmThisWeek", {
          n: rhythmCounts[rhythmCounts.length - 1] ?? 0,
          defaultValue: "This week so far: {{n}}",
        })
      : null;
  const average =
    full.length === 0
      ? null
      : // The mean names its own window (Antenore, 29/09): the weeks it is
        // computed on, not the bars a narrow card happens to show.
        t("mobile.progress.rhythmAverageWeeks", {
          count: full.length,
          avg: (
            Math.round((full.reduce((a, c) => a + c, 0) / full.length) * 10) / 10
          ).toLocaleString(locale),
          defaultValue: "Average over {{count}} full weeks: {{avg}}",
        });
  const summaryLine = [thisWeek, average].filter(Boolean).join(" · ");
  const lastSession: [string, string] = [
    t("mobile.progress.lastSession"),
    summary.lastSession ? formatDate(summary.lastSession.startedAt, dateLocale) : "–",
  ];
  const stats: Array<[string, string]> =
    scope === "period"
      ? [
          [
            t("mobile.progress.report.sessions", {
              defaultValue: "Sessions in the period",
            }),
            String(summary.total),
          ],
          lastSession,
        ]
      : [
          [t("mobile.progress.windowAll"), String(summary.total)],
          [t("mobile.progress.window30d"), String(summary.last30d)],
          [t("mobile.progress.window7d"), String(summary.last7d)],
          lastSession,
        ];
  const kindLabel = (k: RhythmKind) => kindLabels[k];
  const kindTitle = t("mobile.progress.rhythmKind.title", {
    defaultValue: "Sessions counted",
  });
  // A year of weeks still fits a phone: the bars thin out as they multiply.
  const bars =
    rhythmCounts.length <= 13
      ? { barWidth: 12, gap: 4 }
      : rhythmCounts.length <= 26
        ? { barWidth: 8, gap: 3 }
        : { barWidth: 3, gap: 2 };
  return (
    <View style={styles.rhythm}>
      {titleRow}
      {kind && onKind ? (
        <ChipChoiceRow
          theme={theme}
          choices={RHYTHM_KINDS}
          selected={kind}
          onSelect={(k) => {
            tapped.current = k !== kind;
            onKind(k);
          }}
          label={kindLabel}
          chipA11yLabel={kindLabel}
          groupA11yLabel={kindTitle}
          title={kindTitle}
        />
      ) : null}
      {region}
      <View style={styles.statGrid}>
        {stats.map(([label, value]) => (
          <View key={label} style={[styles.stat, oneColumn && styles.statFull]}>
            <Text style={styles.statLabel}>{label}</Text>
            <Text style={styles.statValue}>{value}</Text>
          </View>
        ))}
      </View>
      <View style={styles.card}>
        <Text style={styles.cardTitle}>
          {t("mobile.progress.frequencyTitle")}
        </Text>
        {/* #388: on Progress the bars' own head and ticks say it; only the
            report's bars, without numbers, need the line. */}
        {scope === "period" ? (
          <Text style={styles.cardHelp}>
            {t("mobile.progress.report.frequencyHelp", {
              defaultValue: "Sessions per week across the period.",
            })}
          </Text>
        ) : null}
        {scope === "period" ? (
          <MiniTrendBars
            theme={theme}
            weeks={rhythmCounts}
            height={64}
            barWidth={bars.barWidth}
            gap={bars.gap}
            lastColor={theme.palette.brand500}
            partialLast={partialLast}
          />
        ) : (
          <WeekBarsCard
            theme={theme}
            title={t("mobile.progress.weekBars.sessions", { defaultValue: "Sessions" })}
            values={rhythmCounts}
            nowMs={nowMs}
            weekStartDay={weekStartDay}
            valueFormat={String}
            partialLast={partialLast}
          />
        )}
        {/* The caption under the bars (Antenore 28/09). */}
        {summaryLine ? <Text style={styles.cardHelp}>{summaryLine}</Text> : null}
      </View>
    </View>
  );
}
